/**
 * The onboarding pane's landing page.
 *
 * Scope, stated up front because it is easy to conflate with the Buildium migration
 * described in CLAUDE.md: this pane seeds `owners` and `tenants` contact records —
 * the people, not the money. Security deposits held and open AR by lease are a
 * separate reconciliation build artifact, matched to the penny, run out of band. A
 * CSV or manual entry here creates a record exactly like the "Add owner" / "Add
 * tenant" forms already do; it just does several at once.
 *
 * Gated on `import:write`, which only ADMIN holds — see the comment on that
 * capability in roles.ts.
 */

import { Link } from 'react-router';
import { useBrand } from '../../../lib/brand.js';

interface ChoiceCardProps {
  to: string;
  title: string;
  description: string;
  badge?: string;
}

function ChoiceCard({ to, title, description, badge }: ChoiceCardProps) {
  return (
    <Link to={to} className="choice-card">
      <span className="choice-card__title">
        {title}
        {badge ? (
          <>
            {' '}
            <span className="pill">{badge}</span>
          </>
        ) : null}
      </span>
      <span className="muted">{description}</span>
    </Link>
  );
}

export function ImportHome() {
  const { product } = useBrand();
  return (
    <section className="stack">
      <h1>Onboard owners &amp; tenants</h1>

      <p className="muted">
        Bring owner and tenant contact records into {product.name} — from Buildium, from a
        spreadsheet, or sing manual entry.
      </p>

      <div className="card">
        <strong>Importing can take some time.</strong>
        <p className="muted">Notice: This helper cannot migrate any funds!</p>
        <p className="muted">
          Note: Payment authorizations are not carried over. Every tenant must re-enroll their bank
          account during onboarding, regardless of which path below created their record.
        </p>
      </div>

      <h2>Choose a path</h2>
      <div className="choice-grid">
        <ChoiceCard
          to="/staff/import/buildium"
          title="Connect Buildium"
          description="Pull owner and tenant contact records straight from Buildium's API using a read-only key."
          badge="Not connected yet"
        />
        <ChoiceCard
          to="/staff/import/csv"
          title="Import a CSV"
          description="Upload a CSV export from Buildium or a spreadsheet, map its columns, and review before anything is created."
        />
        <ChoiceCard
          to="/staff/import/manual"
          title="Enter manually"
          description="Type in one owner or tenant, or several at once, with no file needed."
        />
      </div>
    </section>
  );
}
